import { useState, useRef, useCallback, useEffect } from "react";

interface UseClipboardOptions {
  resetDelay?: number;
  successMessage?: string;
  errorMessage?: string;
  pendingMessage?: string;
}

export const useClipboard = (
  text: string,
  {
    resetDelay = 1000,
    successMessage = "Copied!",
    errorMessage = "Failed to copy",
    pendingMessage = "Click to copy",
  }: UseClipboardOptions = {}
) => {
  const [status, setStatus] = useState(pendingMessage);
  const timeoutRef = useRef<number>(undefined);

  const clearExistingTimeout = useCallback(() => {
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  const resetStatus = useCallback(() => {
    clearExistingTimeout();

    timeoutRef.current = window.setTimeout(() => {
      setStatus(pendingMessage);
      timeoutRef.current = undefined;
    }, resetDelay);
  }, [pendingMessage, resetDelay, clearExistingTimeout]);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setStatus(successMessage);
    } catch {
      setStatus(errorMessage);
    } finally {
      resetStatus();
    }
  }, [text, successMessage, errorMessage, resetStatus]);

  useEffect(() => {
    return () => {
      clearExistingTimeout();
    };
  }, [clearExistingTimeout]);

  return { copy, status };
};
